/* ──────────────────────────────────────────────────────────────────
 * Luxury micro-sounds for JS Fragrance.
 * - Generated entirely with the Web Audio API (no files, no licensing).
 * - Volume capped low so nothing feels intrusive.
 * - Persisted on/off via localStorage. Default = ON.
 * ────────────────────────────────────────────────────────────────── */

const STORAGE_KEY = 'jsf_sound_enabled';
const MASTER_VOLUME = 0.12; // 12% — calibrated to feel premium, never loud

let audioCtx = null;
function ctx() {
  if (audioCtx) return audioCtx;
  if (typeof window === 'undefined') return null;
  const Klass = window.AudioContext || window.webkitAudioContext;
  if (!Klass) return null;
  try { audioCtx = new Klass(); } catch { return null; }
  return audioCtx;
}

export function isSoundEnabled() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === null ? true : v === 'true'; // default ON
  } catch { return true; }
}

export function setSoundEnabled(on) {
  try { localStorage.setItem(STORAGE_KEY, String(!!on)); } catch {}
  // Notify listeners (header toggle button)
  try { window.dispatchEvent(new CustomEvent('jsf-sound-toggle', { detail: { enabled: !!on } })); } catch {}
}

/** Play a single tone. Volume is automatically scaled by MASTER_VOLUME. */
function tone({ freq, duration = 0.18, type = 'sine', startTime = 0, attack = 0.005, release = 0.12, gainScale = 1 }) {
  const ac = ctx();
  if (!ac) return;
  const now = ac.currentTime + startTime;
  const osc = ac.createOscillator();
  const gain = ac.createGain();

  osc.type = type;
  osc.frequency.setValueAtTime(freq, now);

  const peak = MASTER_VOLUME * gainScale;
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(peak, now + attack);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + duration + release);

  osc.connect(gain).connect(ac.destination);
  osc.start(now);
  osc.stop(now + duration + release + 0.05);
}

/** Play a chord / sequence of tones. */
function playSequence(notes) {
  if (!isSoundEnabled()) return;
  const ac = ctx();
  if (!ac) return;
  // Resume context if suspended (browser autoplay restrictions)
  if (ac.state === 'suspended') ac.resume().catch(() => {});
  for (const n of notes) tone(n);
}

/* ─── Preset luxury sounds ───────────────────────────────────────── */

/** 🥃 Soft glass "tink" — Add to cart */
export const playAddToCart = () => playSequence([
  { freq: 1318.5, duration: 0.05, type: 'sine',     attack: 0.002, release: 0.18, gainScale: 0.9 },  // E6
  { freq: 1760,   duration: 0.05, type: 'sine',     attack: 0.002, release: 0.22, gainScale: 0.7, startTime: 0.04 }, // A6 — overtone for crystal feel
]);

/** ✨ Ascending celebration — Order placed / Thank-you page */
export const playOrderSuccess = () => playSequence([
  { freq: 523.25, duration: 0.10, type: 'sine',     attack: 0.005, release: 0.20, gainScale: 0.9 },  // C5
  { freq: 659.25, duration: 0.10, type: 'sine',     attack: 0.005, release: 0.22, gainScale: 0.95, startTime: 0.10 }, // E5
  { freq: 783.99, duration: 0.10, type: 'sine',     attack: 0.005, release: 0.25, gainScale: 1.0,  startTime: 0.20 }, // G5
  { freq: 1046.5, duration: 0.18, type: 'sine',     attack: 0.005, release: 0.55, gainScale: 1.1,  startTime: 0.30 }, // C6 — final shimmer
  // Sparkle harmonic on top
  { freq: 2093,   duration: 0.10, type: 'triangle', attack: 0.005, release: 0.45, gainScale: 0.4,  startTime: 0.30 }, // C7
]);

/** 🤏 Tiny click pop — Quiz option click */
export const playClickSoft = () => playSequence([
  { freq: 1100, duration: 0.025, type: 'sine', attack: 0.001, release: 0.05, gainScale: 0.55 },
]);

/** ✨ Magical 3-note arpeggio — Quiz complete / Find your scent */
export const playReveal = () => playSequence([
  { freq: 587.33, duration: 0.12, type: 'sine', attack: 0.005, release: 0.25, gainScale: 0.85 }, // D5
  { freq: 880,    duration: 0.12, type: 'sine', attack: 0.005, release: 0.30, gainScale: 0.95, startTime: 0.10 }, // A5
  { freq: 1174.66,duration: 0.20, type: 'sine', attack: 0.005, release: 0.55, gainScale: 1.0,  startTime: 0.20 }, // D6
  { freq: 2349,   duration: 0.10, type: 'triangle', attack: 0.005, release: 0.45, gainScale: 0.30, startTime: 0.20 }, // sparkle harmonic
]);

/** 💛 Gentle heart note — Wishlist add */
export const playWishlist = () => playSequence([
  { freq: 880,    duration: 0.08, type: 'sine', attack: 0.005, release: 0.20, gainScale: 0.85 }, // A5
  { freq: 1318.5, duration: 0.10, type: 'sine', attack: 0.005, release: 0.30, gainScale: 0.75, startTime: 0.05 }, // E6
]);

/** 🪶 Soft low warble — Error (never harsh) */
export const playError = () => playSequence([
  { freq: 392, duration: 0.10, type: 'triangle', attack: 0.005, release: 0.18, gainScale: 0.7 }, // G4
  { freq: 349.23, duration: 0.12, type: 'triangle', attack: 0.005, release: 0.22, gainScale: 0.7, startTime: 0.08 }, // F4
]);

/** 🚪 Page enter — for important pages (very subtle whoosh) */
export const playPageEnter = () => playSequence([
  { freq: 740, duration: 0.08, type: 'sine', attack: 0.020, release: 0.40, gainScale: 0.45 },
]);
